import { useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { ensure, useNotifications } from "sprint-common-web";

import { UpdateOnPremAssetContentRequest } from "../../../../../../common/api/master-api/assets/details/UpdateOnPremAssetContentRequest";
import { ValidateOnPremAssetContentRequest } from "../../../../../../common/api/master-api/assets/details/ValidateOnPremAssetContentRequest";
import {
    isAncillaryContentDetails,
    isFullTvSeriesContentDetails,
    isMovieContentDetails
} from "../../../../../../common/types/ContentDetails";
import { ContentReference } from "../../../../../../common/types/ContentReference";
import { ContentReferenceType } from "../../../../../../common/types/ContentReferenceType";
import { ContentSubtype } from "../../../../../../common/types/ContentSubtype";
import { ContentAssociationFormState } from "../../../../../../content-ingestion/common/content/types/ContentAssociationFormState";
import { getOnPremAssetDetailsPageHref } from "../../../../../AssetsRoutes";
import { useOnPremAssetDetails } from "../../../common/hooks/useOnPremAssetDetails";
import { resolveOnPremAssetHref } from "../../../common/resolveOnPremAssetHref";
import { OnPremAssetDetails } from "../../../OnPremAssetDetails";
import { useEditOnPremAssetContent } from "./useEditOnPremAssetContent";

export const useEditOnPremAssetContentForm = () => {
    const [defaultContentState, setDefaultContentState] = useState<ContentAssociationFormState>(
        {} as ContentAssociationFormState
    );

    const { success, error } = useNotifications();

    const [isDefaultContentLoading, setIsDefaultContentLoading] = useState<boolean>(true);

    const { data: assetDetails } = useOnPremAssetDetails();
    const { validateOnPremAssetContent, updateOnPremAssetContent } = useEditOnPremAssetContent({
        invalidationKey: assetDetails?.assetId
    });

    const { assetId } = useParams<{ assetId: string }>();
    const history = useHistory();

    const onSubmit = async (contentReference?: ContentReference) => {
        ensure(contentReference);

        const request: UpdateOnPremAssetContentRequest = {
            assetId,
            contentReference
        };

        try {
            const result = await updateOnPremAssetContent.mutateAsync(request);

            goToNewAssetDetails(result.newAssetId, result.newAssetContentSubtype);

            success("New asset created successfully");
        } catch (e) {
            error("Something went wrong while creating asset");
        }
    };

    const validate = (contentReference: ContentReference) => {
        const request: ValidateOnPremAssetContentRequest = {
            assetId,
            contentReference
        };

        return validateOnPremAssetContent.mutateAsync(request);
    };

    const backToAssetDetails = () => {
        const path = getOnPremAssetDetailsPageHref(assetId);
        history.push(path);
    };

    const goToNewAssetDetails = (newAssetId: string, newAssetContentSubtype: ContentSubtype) => {
        const newHref = resolveOnPremAssetHref(newAssetContentSubtype, newAssetId);
        history.push(newHref);
    };

    return {
        defaultContentState,
        setDefaultContentState,
        isDefaultContentLoading,
        assetId,
        setIsDefaultContentLoading,
        backToAssetDetails,
        goToNewAssetDetails,
        onSubmit,
        validate,
        assetDetails
    };
};

export const mapToContentAssociationFormState = (
    onPremAssetDetails: OnPremAssetDetails
): ContentAssociationFormState => {
    const { content } = onPremAssetDetails;

    if (isMovieContentDetails(content)) {
        return {
            contentType: ContentReferenceType.Movie,
            contentId: content.movie.id,
            versionIds: content.versionSummary.ids
        };
    }

    if (isFullTvSeriesContentDetails(content)) {
        return {
            contentType: ContentReferenceType.TvSeries,
            contentId: content.tvSeries.id,
            episodeId: content.episode.id,
            seasonId: content.season.id,
            versionIds: content.versionSummary.ids
        };
    }

    if (isAncillaryContentDetails(content)) {
        return {
            contentType: ContentReferenceType.Ancillary,
            contentId: content.ancillary.id,
            airlineId: content.airline.id
        };
    }

    throw new Error("Unsupported content subtype");
};
